import { useState, useRef, useCallback, useEffect } from "react";
import { Download, Share2, X, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { Button } from "./Button";

// Reflective messages to rotate through
const PRESENCE_MESSAGES = [
  {
    main: "Today I paused and reconnected with presence.",
    sub: "Take a breath.\nLife is already supporting you."
  },
  {
    main: "A moment of stillness in a busy world.",
    sub: "The peace you seek\nis already within you."
  },
  {
    main: "I chose presence today.",
    sub: "In this breath,\neverything is enough."
  },
  {
    main: "Today I remembered to slow down.",
    sub: "There is nowhere to rush.\nYou are already home."
  },
  {
    main: "A pause. A breath. A return to self.",
    sub: "This moment is complete\njust as it is."
  }
];

// Nature background images (high-quality, royalty-free)
const NATURE_BACKGROUNDS = [
  {
    id: "forest",
    name: "Misty Forest",
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=1080&q=80",
    overlay: "rgba(45, 74, 42, 0.55)"
  },
  {
    id: "ocean",
    name: "Ocean Sunrise",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1080&q=80",
    overlay: "rgba(30, 58, 95, 0.50)"
  },
  {
    id: "mountains",
    name: "Mountain Peaks",
    url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1080&q=80",
    overlay: "rgba(55, 65, 81, 0.50)"
  },
  {
    id: "meadow",
    name: "Golden Meadow",
    url: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=1080&q=80",
    overlay: "rgba(74, 103, 65, 0.45)"
  },
  {
    id: "lake",
    name: "Serene Lake",
    url: "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=1080&q=80",
    overlay: "rgba(30, 41, 59, 0.50)"
  }
];

/**
 * Share a Moment of Presence Card
 * Generates a shareable image card with nature backgrounds for social media
 */
export const SharePresenceCard = ({ 
  userReflection = null, 
  onClose,
  isVisible = false 
}) => {
  const canvasRef = useRef(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [includeReflection, setIncludeReflection] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [backgroundImages, setBackgroundImages] = useState({});
  const [loadingBg, setLoadingBg] = useState(true);

  const currentMessage = PRESENCE_MESSAGES[currentMessageIndex];
  const currentBackground = NATURE_BACKGROUNDS[currentBgIndex];

  // Load the Reclaim logo
  useEffect(() => {
    if (isVisible && !logoImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setLogoImage(img);
      img.onerror = () => console.log("Logo load error");
      img.src = "/assets/reclaim-logo-white.png";
    }
  }, [isVisible, logoImage]);

  // Preload background images
  useEffect(() => {
    if (isVisible) {
      setLoadingBg(true);
      const loadedImages = {};
      let loadedCount = 0;
      
      NATURE_BACKGROUNDS.forEach((bg) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          loadedImages[bg.id] = img;
          loadedCount++;
          if (loadedCount === NATURE_BACKGROUNDS.length) {
            setBackgroundImages(loadedImages);
            setLoadingBg(false);
          }
        };
        img.onerror = () => {
          loadedCount++;
          if (loadedCount === NATURE_BACKGROUNDS.length) {
            setBackgroundImages(loadedImages);
            setLoadingBg(false);
          }
        };
        img.src = bg.url;
      });
    }
  }, [isVisible]);

  // Generate the card image on canvas with nature background
  const generateCard = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1920;
    
    canvas.width = width;
    canvas.height = height;

    // Draw background image or fallback gradient
    const bgImage = backgroundImages[currentBackground.id];
    if (bgImage) {
      // Draw the nature background image (cover the canvas)
      const imgRatio = bgImage.width / bgImage.height;
      const canvasRatio = width / height;
      
      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
      
      if (imgRatio > canvasRatio) {
        drawHeight = height;
        drawWidth = height * imgRatio;
        offsetX = -(drawWidth - width) / 2;
      } else {
        drawWidth = width;
        drawHeight = width / imgRatio;
        offsetY = -(drawHeight - height) / 2;
      }
      
      ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight);
      
      // Apply color overlay for text readability
      ctx.fillStyle = currentBackground.overlay;
      ctx.fillRect(0, 0, width, height);
      
      // Add subtle vignette effect
      const vignette = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, height * 0.7);
      vignette.addColorStop(0, 'rgba(0,0,0,0)');
      vignette.addColorStop(1, 'rgba(0,0,0,0.3)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, width, height);
    } else {
      // Fallback gradient if image not loaded
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#2D4A2A');
      gradient.addColorStop(1, '#1A2D18');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    // Add subtle grain texture
    ctx.globalAlpha = 0.04;
    for (let i = 0; i < 8000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#000000';
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Draw logo (white version for dark backgrounds)
    if (logoImage) {
      const logoHeight = 50;
      const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
      const logoX = (width - logoWidth) / 2;
      const logoY = 165;
      ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
      
      // Tagline under logo
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 24px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('School of Consciousness', width / 2, logoY + logoHeight + 35);
    } else {
      // Fallback text logo
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '600 56px "Playfair Display", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('RECLAIM', width / 2, 220);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 24px "Inter", sans-serif';
      ctx.fillText('School of Consciousness', width / 2, 270);
    }

    // Main message (white text on nature background)
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '500 64px "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;
    
    // Word wrap the main message
    const mainLines = wrapText(ctx, currentMessage.main, width - 160, 64);
    let yPos = 700;
    mainLines.forEach(line => {
      ctx.fillText(line, width / 2, yPos);
      yPos += 80;
    });

    // Reset shadow for sub text
    ctx.shadowBlur = 10;
    
    // Sub message (with line breaks)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = 'italic 400 42px "Playfair Display", Georgia, serif';
    yPos += 80;
    
    const subLines = currentMessage.sub.split('\n');
    subLines.forEach(line => {
      ctx.fillText(line, width / 2, yPos);
      yPos += 60;
    });

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // User reflection (if included and exists)
    if (includeReflection && userReflection) {
      yPos += 100;
      
      // Quote box background (semi-transparent white)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      const boxWidth = width - 160;
      ctx.beginPath();
      ctx.roundRect((width - boxWidth) / 2, yPos - 40, boxWidth, 200, 24);
      ctx.fill();

      // Quote marks
      ctx.fillStyle = 'rgba(212, 163, 115, 0.8)'; // Warm gold
      ctx.font = '300 120px Georgia, serif';
      ctx.fillText('"', 120, yPos + 50);

      // User's reflection (truncated)
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'italic 400 36px "Playfair Display", Georgia, serif';
      const truncatedReflection = userReflection.length > 100 
        ? userReflection.substring(0, 100) + '...'
        : userReflection;
      const reflectionLines = wrapText(ctx, truncatedReflection, boxWidth - 120, 36);
      let refY = yPos + 30;
      reflectionLines.slice(0, 3).forEach(line => {
        ctx.fillText(line, width / 2, refY);
        refY += 48;
      });
    }

    // Decorative line
    ctx.strokeStyle = 'rgba(212, 163, 115, 0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 60, height - 240);
    ctx.lineTo(width / 2 + 60, height - 240);
    ctx.stroke();

    // "Shared from Reclaim" branding
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '400 28px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Shared from Reclaim', width / 2, height - 180);
    
    // URL
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '400 22px "Inter", sans-serif';
    ctx.fillText('reclaim.app', width / 2, height - 140);

    return canvas.toDataURL('image/png');
  }, [currentMessage, currentBackground, includeReflection, userReflection, logoImage, backgroundImages]);

  // Helper function for text wrapping
  const wrapText = (ctx, text, maxWidth, fontSize) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };

  // Update preview when options change
  const updatePreview = useCallback(async () => {
    if (loadingBg) return;
    setIsGenerating(true);
    const url = await generateCard();
    setPreviewUrl(url);
    setIsGenerating(false);
  }, [generateCard, loadingBg]);

  // Generate preview on mount and when options change
  useEffect(() => {
    if (isVisible && !loadingBg) {
      updatePreview();
    }
  }, [isVisible, loadingBg, updatePreview, currentBgIndex, currentMessageIndex, includeReflection]);

  // Regenerate when visible or options change
  const handleMessageChange = (direction) => {
    const newIndex = (currentMessageIndex + direction + PRESENCE_MESSAGES.length) % PRESENCE_MESSAGES.length;
    setCurrentMessageIndex(newIndex);
  };

  const handleBackgroundChange = (direction) => {
    const newIndex = (currentBgIndex + direction + NATURE_BACKGROUNDS.length) % NATURE_BACKGROUNDS.length;
    setCurrentBgIndex(newIndex);
  };

  const handleReflectionToggle = () => {
    setIncludeReflection(!includeReflection);
  };

  // Download image
  const handleDownload = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    const link = document.createElement('a');
    link.download = 'moment-of-presence.png';
    link.href = dataUrl;
    link.click();
    
    setIsGenerating(false);
  };

  // Share via native share sheet (Instagram, WhatsApp, Messages, X, etc.)
  const handleShare = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'moment-of-presence.png', { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'A Moment of Presence',
          text: `${currentMessage.main} - Shared from Reclaim`
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to download if share fails
          handleDownload();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      handleDownload();
    }
    
    setIsGenerating(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-sm w-full max-h-[90vh] flex flex-col shadow-2xl animate-fade-in-up">
        {/* Header - fixed at top */}
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] shrink-0">
          <h3 className="font-heading text-lg text-[#1F2937]">Share a Moment of Presence</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F3F4F6] rounded-full transition-colors"
            data-testid="close-share-modal"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Preview - smaller aspect ratio for better fit */}
          <div className="relative aspect-[3/4] bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] rounded-2xl overflow-hidden shadow-inner">
            {previewUrl && !loadingBg ? (
              <img 
                src={previewUrl} 
                alt="Share card preview" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            
            {/* Message navigation */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <button
                onClick={() => handleMessageChange(-1)}
                className="w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                data-testid="prev-message"
              >
                <ChevronLeft size={18} className="text-[#4A6741]" />
              </button>
              <span className="text-xs text-white/80 bg-black/20 px-3 py-1 rounded-full backdrop-blur">
                {currentMessageIndex + 1} / {PRESENCE_MESSAGES.length}
              </span>
              <button
                onClick={() => handleMessageChange(1)}
                className="w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                data-testid="next-message"
              >
                <ChevronRight size={18} className="text-[#4A6741]" />
              </button>
            </div>
          </div>

          {/* Background selector */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <ImageIcon size={16} />
              <span>Background:</span>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <button
                onClick={() => handleBackgroundChange(-1)}
                className="p-1.5 hover:bg-[#F3F4F6] rounded-full transition-colors"
              >
                <ChevronLeft size={16} className="text-[#6B7280]" />
              </button>
              <span className="flex-1 text-center text-sm font-medium text-[#1F2937]">
                {currentBackground.name}
              </span>
              <button
                onClick={() => handleBackgroundChange(1)}
                className="p-1.5 hover:bg-[#F3F4F6] rounded-full transition-colors"
              >
                <ChevronRight size={16} className="text-[#6B7280]" />
              </button>
            </div>
          </div>

          {/* Background thumbnails */}
          <div className="flex gap-2 justify-center">
            {NATURE_BACKGROUNDS.map((bg, idx) => (
              <button
                key={bg.id}
                onClick={() => setCurrentBgIndex(idx)}
                className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === currentBgIndex 
                    ? 'border-[#4A6741] scale-105' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img 
                  src={bg.url} 
                  alt={bg.name}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Include reflection toggle */}
          {userReflection && (
            <label className="flex items-center gap-3 p-4 bg-[#F9FAFB] rounded-xl cursor-pointer group">
              <input
                type="checkbox"
                checked={includeReflection}
                onChange={handleReflectionToggle}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                ${includeReflection 
                  ? 'bg-[#4A6741] border-[#4A6741]' 
                  : 'border-[#D1D5DB] group-hover:border-[#4A6741]'
                }`}
              >
                {includeReflection && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1F2937]">Include my reflection</p>
                <p className="text-xs text-[#6B7280] line-clamp-1">"{userReflection}"</p>
              </div>
            </label>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={isGenerating || loadingBg}
              className="flex-1"
              data-testid="download-card-btn"
            >
              <Download size={18} />
              Save
            </Button>
            <Button
              onClick={handleShare}
              disabled={isGenerating || loadingBg}
              className="flex-1"
              data-testid="share-card-btn"
            >
              <Share2 size={18} />
              Share
            </Button>
          </div>

          <p className="text-xs text-center text-[#9CA3AF]">
            Share to Instagram, WhatsApp, Messages, X & more
          </p>
        </div>

        {/* Hidden canvas for image generation */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default SharePresenceCard;
